import { Routes } from '@angular/router';
import { DashboardPageComponent } from './routes/internal/dashboard/dashboard.component';
import { InternalComponent } from './routes/internal/internal.component';
import { SettingsPageComponent } from './routes/internal/settings/settings.component';
import { WebinarTemplatesPageComponent } from './routes/internal/webinar-templates/webinar-templates.component';
import { WebinarsLayoutComponent } from './routes/internal/webinars/webinars.layout';
import { PublicComponent } from './routes/public/public.component';
import { ClientSubscriptionFlag } from '@shared/enums/client-subscription-flag';
import {
  ClientPermission,
  OwnPermission,
  RecordingPermission,
  WebinarPermission,
} from '@shared/enums/permission.enum';
import { authGuard } from '@shared/guards/auth.guard';
import { clientSubscriptionFeatureGuard } from '@shared/guards/client-subscription-feature.guard';
import { featureFlagGuard } from '@shared/guards/feature-flag.guard';
import { permissionGuard } from '@shared/guards/permission.guard';
import { ForbiddenPageComponent } from '@shared/pages/forbidden/forbidden.component';
import { GeneralErrorPageComponent } from '@shared/pages/general-error/general-error.component';
import { MissingFeaturePageComponent } from '@shared/pages/missing-feature/missing-feature.component';
import { UrlNotFoundPageComponent } from '@shared/pages/url-not-found/url-not-found.component';
import { FLAGS } from '@shared/services/growthbook/flags';

export const routes: Routes = [
  {
    path: 'public',
    children: [
      {
        path: '',
        component: PublicComponent,
      },
    ],
  },
  {
    path: '',
    canMatch: [authGuard],
    component: InternalComponent,
    children: [
      {
        path: '',
        component: DashboardPageComponent,
      },
      {
        path: 'webinars',
        component: WebinarsLayoutComponent,
        canActivate: [permissionGuard, featureFlagGuard],
        data: {
          requiredFlags: [FLAGS.ENABLE_WEBINAR_MANAGEMENT_UI],
        },
        loadChildren: () => {
          return import('./routes/internal/webinars/webinars.routes').then(m => m.ROUTES);
        },
      },
      {
        path: 'webinar-templates',
        component: WebinarTemplatesPageComponent,
        canActivate: [clientSubscriptionFeatureGuard],
        data: {
          clientSubscriptionFeatureFlag: ClientSubscriptionFlag.WebinarTemplatesEnabled,
          requiredFeatureFlags: [FLAGS.ENABLE_WEBINAR_TEMPLATES_MANAGEMENT_UI],
        },
        loadChildren: () => {
          return import('./routes/internal/webinar-templates/webinar-templates.routes').then(
            m => m.ROUTES,
          );
        },
      },
      {
        path: 'recordings',
        canActivate: [permissionGuard, featureFlagGuard],
        data: {
          permissions: [RecordingPermission.View],
          requiredFlags: [FLAGS.ENABLE_RECORDING_MANAGEMENT_UI],
        },
        loadChildren: () => {
          return import('./routes/internal/recordings/recordings.routes').then(m => m.ROUTES);
        },
      },
      {
        path: 'automations',
        // canMatch (not canActivate): checked before the lazy chunk is downloaded and on every navigation.
        canMatch: [permissionGuard, featureFlagGuard, clientSubscriptionFeatureGuard],
        data: {
          permissions: [ClientPermission.Settings],
          clientSubscriptionFeatureFlag: ClientSubscriptionFlag.AutomationTemplatingEnabled,
          requiredFlags: [FLAGS.ENABLE_AUTOMATION_MANAGEMENT_UI],
        },
        loadChildren: () => {
          return import('./routes/internal/automations/automations.routes').then(m => m.ROUTES);
        },
      },
      {
        path: 'participants',
        canActivate: [permissionGuard, featureFlagGuard],
        data: {
          permissions: [WebinarPermission.View, OwnPermission.WebinarViewOwn],
          requiredFlags: [FLAGS.ENABLE_PARTICIPANTS_UI],
        },
        loadChildren: () => {
          return import('./routes/internal/participants/participants.routes').then(m => m.ROUTES);
        },
      },
      {
        path: 'monetization',
        canActivate: [permissionGuard, featureFlagGuard],
        data: {
          permissions: [ClientPermission.Settings],
          requiredFlags: [FLAGS.ENABLE_MONETIZATION_MANAGEMENT_UI],
        },
        loadChildren: () => {
          return import('./routes/internal/monetization/monetization.routes').then(m => m.ROUTES);
        },
      },
      {
        path: 'settings',
        component: SettingsPageComponent,
        canActivate: [permissionGuard, featureFlagGuard],
        data: {
          permissions: [ClientPermission.Settings],
          requiredFlags: [FLAGS.ENABLE_SETTINGS_MANAGEMENT_UI],
        },
        loadChildren: () => {
          return import('./routes/internal/settings/settings.routes').then(m => m.ROUTES);
        },
      },
      {
        path: 'forbidden',
        component: ForbiddenPageComponent,
      },
      {
        path: 'missing-feature',
        component: MissingFeaturePageComponent,
      },
      {
        path: 'error',
        component: GeneralErrorPageComponent,
      },
    ],
  },
  {
    path: '**',
    component: UrlNotFoundPageComponent,
  },
];
